/**
 * Shared API client utilities
 *
 * Provides authentication headers and standardized fetch wrapper
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { buildQueryString, buildPaginationQuery } from '@lognai/frontend-core';

export { buildQueryString, buildPaginationQuery };
export type { ListQueryParams } from '@lognai/frontend-core';

/**
 * Standardized fetch wrapper with error handling
 */
export const apiFetch = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized' });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, error.details);
  }

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', (apiResponse as any).details);
  }

  return apiResponse.data;
};
